import { useMemo, useRef } from "react";

import { FormAIFillSettings, StaffDetails } from "@smart/bridge-types-basic";
import { useIsMobile } from "@smart/itops-hooks-dom";
import { Select } from "@smart/itops-sb-design-system-dom";
import { mapEntries } from "@smart/itops-utils-basic";

import { AutofillUnavailableModal } from "./autofill-components";
import { FieldComponentOverrideProps, FieldProps } from "./field-components";
import {
  IntakeFormAutoFill,
  IntakeFormSection,
  IntakeFormSummary,
} from "./form-components";
import { Heading } from "./heading";
import { ScrollWrapper } from "./section-components";
import { TeamLabel } from "./shared-components/team-label";
import { IntakeSidebar } from "./sidebar";
import { IntakeSignaturePreparingStatus } from "./signature-preparing-status";
import { IntakeStatus } from "./status";
import {
  IntakeFormHookProps,
  useIntakeForm,
  BrandingProvider,
  Branding,
  ProviderInfoProvider,
  VoiceConversationProvider,
  VoiceConversationWebSocketApi,
} from "../hooks";
import {
  FieldItem,
  IntakeFormSectionRef,
  LookupOptions,
  SectionItem,
  TeamItem,
  Toast,
} from "../types";
import { getNativeLanguageName } from "../utils";

export type IntakeFormProps = IntakeFormHookProps & {
  isMatterClosed?: boolean;
  form: {
    uri: string;
    team: TeamItem;
    hideTeam?: boolean;
    response?: string;
    category?: string;
    aiFillSettings?: FormAIFillSettings;
    title?: string;
    translations?: {
      language: string;
      title?: string;
      response?: string;
      aiFillSectionTitle?: string;
      aiFillFileUploadLabel?: string;
      aiFillFileUploadDescription?: string;
    }[];
    supportedLanguages?: {
      key: string;
      label: string;
    }[];
    matterTypes?: {
      representativeOptions?: string[];
    }[];
    redirectUrl?: string;
  };
  lookup: LookupOptions;
  branding?: Branding;
  previewOptions?: {
    previewErrorToggle?: [
      boolean,
      React.Dispatch<React.SetStateAction<boolean>>,
    ];
    previewResponses?: [
      Record<string, any>,
      React.Dispatch<React.SetStateAction<Record<string, any>>>,
    ];
    previewLastSection: [
      SectionItem | "summary" | undefined,
      React.Dispatch<React.SetStateAction<SectionItem | "summary" | undefined>>,
    ];
  };
  isInternal?: boolean;
  newUI?: boolean;
  staffDetails: StaffDetails[];
  intakeFormKey?: string;
  isPreview?: boolean;
  showPaymentPlaceholder?: boolean;
  loading?: boolean;
  isAutoFillFileUploadDisabled?: boolean;
  fieldComponentOverride?: (
    fieldProps: FieldProps & FieldComponentOverrideProps,
  ) => React.ReactNode | undefined;
  setToasts?: (toasts: Toast[]) => void;
  setFields?: (setFn: (fields: FieldItem[]) => FieldItem[]) => void;
  VoiceConversationComponent?: React.ComponentType;
  voiceWebSocket?: VoiceConversationWebSocketApi;
  selectedLanguage?: string;
  setSelectedLanguage?: (language: string) => void;
  disableSignatureEditing?: boolean;
  hasPendingDigitalSignature?: boolean;
  signingUrl?: string;
  onSignatureModalClose?: () => void;
};

export const IntakeForm = ({
  isMatterClosed,
  intakeFormKey,
  form,
  lookup,
  previewOptions,
  isInternal,
  newUI,
  staffDetails,
  loading,
  isAutoFillFileUploadDisabled,
  fieldComponentOverride,
  setToasts,
  setFields,
  showPaymentPlaceholder,
  branding,
  VoiceConversationComponent,
  voiceWebSocket,
  selectedLanguage,
  setSelectedLanguage,
  disableSignatureEditing,
  onSignatureModalClose,
  ...formOptions
}: IntakeFormProps) => {
  const {
    summary,
    statusProps,
    sidebarProps,
    sectionProps,
    summaryProps,
    printProps,
    uploadFileProps,
    autoFillProps,
    autoFillUnavailableProps,
    signaturePreparingStatus,
  } = useIntakeForm({
    ...formOptions,
    aiFillSettings: form.aiFillSettings,
    previewLastSection: previewOptions?.previewLastSection,
    submission: {
      ...formOptions.submission,
      responses:
        (previewOptions?.previewResponses?.[0]
          ? mapEntries<Record<string, any>, Record<string, { value: any }>>(
              previewOptions.previewResponses[0],
              (value) => ({
                value,
              }),
            )
          : undefined) || formOptions.submission.responses,
    },
    isPreview: formOptions.isPreview,
    selectedLanguage,
    redirectUrl: form.redirectUrl,
    formTranslations: form.translations,
  });
  const isMobile = useIsMobile();

  const sectionRef = useRef<IntakeFormSectionRef>(null);
  const shouldDisplayFirmLogo = branding?.headerDisplay?.firmLogo !== false;
  const shouldDisplayFirmName = branding?.headerDisplay?.firmName !== false;
  const shouldHideTeamInfo =
    form.hideTeam ||
    formOptions.isPreview ||
    (!shouldDisplayFirmLogo && !shouldDisplayFirmName);
  const brandedHeaderTextColor = branding?.headerTextColor;
  const isHeaderBackgroundBranded = branding?.affectedAreas?.headerBackground;
  const isHeaderUnderlineBranded = branding?.affectedAreas?.headerUnderline;
  const areSectionTabsBranded = branding?.affectedAreas?.sectionTabHighlights;

  const languageOptions = form.supportedLanguages?.length
    ? [
        { label: getNativeLanguageName("en"), value: "en" },
        ...form.supportedLanguages.map((language) => ({
          label: getNativeLanguageName(language.key),
          value: language.key,
        })),
      ].sort((a, b) => a.label.localeCompare(b.label))
    : undefined;

  const previewResponses = previewOptions?.previewResponses?.[0];
  const voiceConversationOptions = useMemo(
    () =>
      VoiceConversationComponent && formOptions.submission.uri && voiceWebSocket
        ? {
            submissionUri: formOptions.submission.uri,
            preview: formOptions.isPreview
              ? {
                  formUri: form.uri,
                  responses: mapEntries(previewResponses ?? {}, (value) => ({
                    value,
                  })) as Record<string, { value: any }>,
                }
              : undefined,
            submitResponses: sectionProps.submitResponses,
            fields: sectionProps.fields,
            sectionRef,
            visibleSections: sectionProps.visibleSections,
            selectedSection: sectionProps.selected,
            onSelectSection: sectionProps.onSelect,
            onSummary: sectionProps.onSummary,
            language: selectedLanguage,
            voiceWebSocket,
            autoFillStatus: formOptions.autoFillStatus,
          }
        : undefined,
    [
      VoiceConversationComponent,
      formOptions.submission.uri,
      voiceWebSocket,
      formOptions.isPreview,
      form.uri,
      previewResponses,
      sectionProps.submitResponses,
      sectionProps.fields,
      sectionProps.visibleSections,
      sectionProps.selected,
      sectionProps.onSelect,
      sectionProps.onSummary,
      selectedLanguage,
      formOptions.autoFillStatus,
    ],
  );

  const renderWithHeader = (content: React.ReactNode) => (
    <ProviderInfoProvider source={form.team.source}>
      <BrandingProvider branding={branding}>
        <ScrollWrapper>
          <Heading
            hidden={shouldHideTeamInfo}
            isHeaderBackgroundBranded={isHeaderBackgroundBranded}
            isHeaderUnderlineBranded={isHeaderUnderlineBranded}
            left={
              <TeamLabel
                teamName={form.team.name}
                teamPicture={branding?.logo || form.team.picture}
                displayTeamName={shouldDisplayFirmName}
                displayTeamPicture={shouldDisplayFirmLogo}
                brandedHeaderTextColor={brandedHeaderTextColor}
              />
            }
          />
          {content}
        </ScrollWrapper>
      </BrandingProvider>
    </ProviderInfoProvider>
  );

  if (signaturePreparingStatus) {
    return renderWithHeader(
      <IntakeSignaturePreparingStatus status={signaturePreparingStatus} />,
    );
  }

  if (
    isMatterClosed ||
    (statusProps.status !== "active" &&
      !isInternal &&
      !statusProps.redirected) ||
    statusProps.deleted
  ) {
    return renderWithHeader(
      <IntakeStatus
        {...form}
        {...statusProps}
        recipient={formOptions.submission.recipient}
        fields={sectionProps.fields}
        responses={sectionProps.responses}
        formMatterTypes={form.matterTypes}
        selectedLanguage={selectedLanguage}
        printOptions={{
          ...printProps,
          content: {
            ...printProps.content,
            formTitle: form.title,
            teamDetails: form.team,
            completedOn: formOptions.submission.updatedAt,
            formTranslations: form.translations,
            selectedLanguage,
          },
        }}
        isMatterClosed={isMatterClosed}
        hasPendingDigitalSignature={!!formOptions.hasPendingDigitalSignature}
        signingUrl={formOptions.signingUrl}
        onSignatureModalClose={onSignatureModalClose}
      />,
    );
  }

  const aiFillFromMatterInfo = !!formOptions.submission.aiFillFromMatterInfo;
  const pageDisabled =
    formOptions.autoFillStatus === "filling" ||
    (aiFillFromMatterInfo && loading);

  const voiceConversation =
    voiceConversationOptions && VoiceConversationComponent ? (
      <VoiceConversationComponent />
    ) : undefined;

  const renderContent = () => {
    if (summary) {
      const { key, ...restSectionProps } = sectionProps;
      return (
        <IntakeFormSummary
          key={key}
          {...form}
          {...summaryProps}
          {...restSectionProps}
          previewOptions={
            previewOptions?.previewResponses &&
            previewOptions?.previewErrorToggle
              ? {
                  previewResponses: previewOptions.previewResponses[0],
                  clearLastPreviewSection: () =>
                    previewOptions.previewLastSection[1](undefined),
                  previewErrorToggle: previewOptions.previewErrorToggle,
                }
              : undefined
          }
          isPreview={formOptions.isPreview}
          isTeamHidden={shouldHideTeamInfo}
          setToasts={setToasts}
          selectedLanguage={selectedLanguage}
        />
      );
    }

    if (autoFillProps.isAutoFillSectionSelected) {
      return (
        <IntakeFormAutoFill
          {...form}
          {...autoFillProps}
          uploadFileProps={uploadFileProps}
          fileLookup={lookup.fileLookup}
          isFileUploadDisabled={isAutoFillFileUploadDisabled}
          isTeamHidden={shouldHideTeamInfo}
          isWebEnquiry={formOptions.submission.communicationMethod === "embed"}
          connectionError={sectionProps.connection?.status === "error"}
          isPreview={formOptions.isPreview}
          voiceConversation={voiceConversation}
          selectedLanguage={selectedLanguage}
          setSelectedLanguage={setSelectedLanguage}
          languageOptions={languageOptions}
        />
      );
    }

    const { key, ...restSectionProps } = sectionProps;
    return (
      <IntakeFormSection
        key={key}
        {...form}
        {...lookup}
        {...restSectionProps}
        uploadFileProps={uploadFileProps}
        ref={sectionRef}
        submissionSyncStatus={formOptions.submission.syncStatus}
        previewOptions={previewOptions}
        staffDetails={staffDetails}
        loadResponses={formOptions.loadResponses}
        aiFillFromMatterInfo={aiFillFromMatterInfo}
        loading={loading}
        isPreview={formOptions.isPreview}
        showPaymentPlaceholder={showPaymentPlaceholder}
        fieldComponentOverride={fieldComponentOverride}
        setToasts={setToasts}
        setFields={setFields}
        isTeamHidden={shouldHideTeamInfo}
        isWebEnquiry={formOptions.submission.communicationMethod === "embed"}
        selectedLanguage={selectedLanguage}
        setSelectedLanguage={setSelectedLanguage}
        languageOptions={languageOptions}
        voiceConversation={voiceConversation}
        isMobile={isMobile}
        disableSignatureEditing={disableSignatureEditing}
      />
    );
  };

  const headerLanguageSelector = languageOptions && setSelectedLanguage && (
    <Select
      id="select-languages-intake-form"
      placeholder="Select language"
      options={languageOptions}
      value={selectedLanguage}
      onChange={setSelectedLanguage}
    />
  );

  const intakeFormContent = (
    <IntakeSidebar
      {...sidebarProps}
      sectionRef={sectionRef}
      newUI={newUI}
      isPreview={formOptions.isPreview}
      isTeamHidden={shouldHideTeamInfo}
      hideErrors={previewOptions?.previewErrorToggle?.[0]}
      areSectionTabsBranded={areSectionTabsBranded}
      selectedLanguage={selectedLanguage}
    >
      <Heading
        hidden={shouldHideTeamInfo}
        isHeaderBackgroundBranded={isHeaderBackgroundBranded}
        isHeaderUnderlineBranded={isHeaderUnderlineBranded}
        left={
          <TeamLabel
            teamName={form.team.name}
            teamPicture={branding?.logo || form.team.picture}
            displayTeamName={shouldDisplayFirmName}
            displayTeamPicture={shouldDisplayFirmLogo}
            brandedHeaderTextColor={brandedHeaderTextColor}
          />
        }
        right={
          !isMobile &&
          (headerLanguageSelector || voiceConversation) && (
            <div className="flex gap-medium items-center">
              {headerLanguageSelector}
              {voiceConversation}
            </div>
          )
        }
      />
      {pageDisabled && (
        <div className="absolute h-full w-full z-80 m-0 p-0 border-0 bg-neutral-200 opacity-40" />
      )}
      {renderContent()}
      <AutofillUnavailableModal {...autoFillUnavailableProps} />
    </IntakeSidebar>
  );

  return (
    <ProviderInfoProvider source={form.team.source}>
      <BrandingProvider branding={branding}>
        {voiceConversationOptions ? (
          <VoiceConversationProvider options={voiceConversationOptions}>
            {intakeFormContent}
          </VoiceConversationProvider>
        ) : (
          intakeFormContent
        )}
      </BrandingProvider>
    </ProviderInfoProvider>
  );
};
